import { useTranslation } from '@/utils/hook'
import { Header } from '@/components/header'
import Image from 'next/image'
import { RiArrowRightLine } from '@remixicon/react'
import { Libre_Baskerville } from 'next/font/google'
import {
  Section,
  SectionContainer,
  SectionTitle
} from '@/components/ui'

const libreBaskerville = Libre_Baskerville({
  subsets: ['latin'],
  weight: ['700'],
  display: 'swap',
  fallback: ['Georgia', 'Times New Roman', 'serif']
})

export function Banner({ githubStars, userCount, creators }): JSX.Element {
  const t = useTranslation()
  const headlineLine1 = t('home.bannerHeadline1')
  const formattedGithubStars = new Intl.NumberFormat().format(githubStars)
  const roundedUserCount =
    userCount >= 1000 ? `${Math.round(userCount / 1000)}k` : `${userCount}`

  return (
    <Section
      className="relative overflow-hidden md:min-h-screen"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at top center, rgba(96, 165, 250, 0.22) 0%, rgba(96, 165, 250, 0) 34%),
            radial-gradient(circle at 18% 22%, rgba(196, 181, 253, 0.18) 0%, rgba(196, 181, 253, 0) 28%),
            radial-gradient(circle at 82% 18%, rgba(125, 211, 252, 0.18) 0%, rgba(125, 211, 252, 0) 30%),
            linear-gradient(180deg, #ffffff 0%, #eef4ff 58%, #e9f1ff 100%)
          `
        }}
      />
      <Header />
      <SectionContainer className="relative z-10 flex flex-col justify-center pt-16 sm:pt-24 md:min-h-screen">

        <div className="mx-auto max-w-5xl text-center">
          <div className="space-y-6">
            <SectionTitle
              size="hero"
              className={`${libreBaskerville.className} mx-auto max-w-3xl`}
              style={libreBaskerville.style}
            >
              {headlineLine1}
            </SectionTitle>
            <div className="mx-auto max-w-2xl text-center text-sm leading-tight tracking-wide text-[rgb(var(--ds-text-muted))] md:text-xl">
              <p>
                Build interactive forms, keep full control of your data, and stop overpaying — the way it should be.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <a
            className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-base font-medium text-white shadow-[0_8px_24px_rgba(15,23,42,0.18)] transition-transform duration-200 hover:bg-slate-700 md:text-lg"
            href="https://my.heyform.net/sign-up"
          >
            <span>{t('common.tryFree')}</span>
            <RiArrowRightLine className="ml-2 inline w-7" />
          </a>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4 text-center">
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-3 text-base font-medium text-[rgb(var(--ds-text))] md:text-lg">
              <Image
                src="/static/stars-4.5.svg"
                alt="HeyForm 4.5 rating"
                quality={100}
                width={180}
                height={32}
                priority
                className="h-6 w-auto md:h-7"
              />
              <span>4.5 rating</span>
            </div>
            <div className="text-sm text-[rgb(var(--ds-text-muted))] md:text-base">
              <span className="font-semibold text-[rgb(var(--ds-text))]">
                {formattedGithubStars}
              </span>{' '}
              GitHub stars
              <span className="mx-2">•</span>
              <span className="font-semibold text-[rgb(var(--ds-text))]">
                {roundedUserCount}+
              </span>{' '}
              users
            </div>
          </div>

          <div className="relative overflow-x-hidden rounded-full border border-input bg-foreground p-1">
            <div className="flex -space-x-3">
              {creators.slice(0, 5).map((creator, index) => (
                <Image
                  key={index}
                  className="inline-block h-12 w-12 rounded-full object-cover"
                  src={creator.avatar}
                  width={100}
                  height={100}
                  quality={100}
                  alt={`Avatar for user ${creator.name}`}
                />
              ))}
            </div>
          </div>
        </div>
        

        <div className="overflow-hidden">
          <Image
            src="/static/banner.webp"
            alt="HeyForm banner"
            className="w-full"
            quality={100}
            width={3840}
            height={2880}
            priority
            unoptimized
          />
        </div>

      </SectionContainer>
    </Section>
  )
}
